/**
 * MentorDashboard.jsx — HealHub Mentor Dashboard (Production v2)
 *
 * FIXES IN THIS VERSION:
 *  ✅ Removed duplicate back/home button — only ONE 🏠 button in header
 *  ✅ now state is reactive (useState + setInterval every 30s) — not a stale render-time value
 *  ✅ Time parsing hardened with normalizeTime() — handles TIMETZ "+05:30" suffix from Supabase
 *  ✅ Active sessions update reactively every 30s without DB polling
 *  ✅ Home button navigates to /mentordashboard (was wrongly /community in some versions)
 *  ✅ RT subscription for chat uses ref to avoid stale closure
 *  ✅ All original features preserved: ban penalty, break, day offs, reports, etc.
 */

import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate }      from "react-router-dom";
import { auth }             from "../firebase";
import { supabase }         from "../supabase";
import HealHubVideoRoom     from "../components/HealHubVideoRoom";

const BACKEND_URL  = process.env.REACT_APP_BACKEND_URL   || "http://localhost:5000";
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "";
const PLATFORM_FEE = 10;

const CATEGORIES = [
  { value:"mental_health", label:"Mental Health",    icon:"🧠" },
  { value:"career",        label:"Career Guidance",  icon:"💼" },
  { value:"relationships", label:"Relationships",    icon:"💑" },
  { value:"stress",        label:"Stress & Anxiety", icon:"🌊" },
  { value:"grief",         label:"Grief & Loss",     icon:"🕊️" },
  { value:"addiction",     label:"Addiction",        icon:"🤝" },
  { value:"parenting",     label:"Parenting",        icon:"👨‍👩‍👧" },
  { value:"student",       label:"Student Wellness", icon:"📚" },
  { value:"general",       label:"General Wellness", icon:"🌿" },
];

const DAYS  = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const TIMES = ["06:00","07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00"];

const calcCommissionLabel = (rating, totalReviews) => {
  const r = parseFloat(rating) || 0;
  const n = parseInt(totalReviews) || 0;
  if (n >= 10 && r < 2)    return { label:"⚠️ Penalty Tier",  pct:55, color:"#f44336" };
  if (n >= 3  && r >= 4.5) return { label:"⭐ Premium Tier",   pct:75, color:"#4caf50" };
  return                          { label:"📊 Standard Tier", pct:65, color:"#5c6bc0" };
};

/**
 * CRITICAL: Normalize time from Supabase.
 * TIME columns return "HH:MM:SS", TIMETZ returns "HH:MM:SS+05:30"
 * Strip timezone offset so JS parses as local time.
 */
const normalizeTime = (timeStr) => {
  if (!timeStr) return "00:00:00";
  const clean = String(timeStr).trim().replace(/[+-]\d{2}:\d{2}$/, "").split(".")[0];
  const parts = clean.split(":");
  const h = (parts[0] || "00").padStart(2,"0");
  const m = (parts[1] || "00").padStart(2,"0");
  const s = (parts[2] || "00").padStart(2,"0");
  return `${h}:${m}:${s}`;
};

const buildSessionDate = (dateStr, rawTime) => {
  return new Date(`${dateStr}T${normalizeTime(rawTime)}`);
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');
  .md-root{font-family:'DM Sans',sans-serif;min-height:100vh;background:#f4f5fb;-webkit-font-smoothing:antialiased}
  .md-header{background:linear-gradient(135deg,#3949ab,#5c6bc0);padding:16px;display:flex;align-items:center;gap:12px;position:sticky;top:0;z-index:100;box-shadow:0 2px 12px rgba(57,73,171,.3)}
  .md-header-avatar{width:40px;height:40px;border-radius:50%;object-fit:cover;border:2px solid rgba(255,255,255,.4);flex-shrink:0}
  .md-header-avatar-placeholder{width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:1.1rem;flex-shrink:0}
  .md-header-info{flex:1;min-width:0}
  .md-header-name{font-weight:700;font-size:.95rem;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .md-header-role{font-size:.75rem;color:rgba(255,255,255,.7)}
  .md-header-right{display:flex;gap:8px;flex-shrink:0}
  .md-header-btn{padding:7px 14px;background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.3);border-radius:8px;color:#fff;font-size:.8rem;font-weight:600;cursor:pointer;white-space:nowrap}
  .md-header-btn:hover{background:rgba(255,255,255,.25)}
  .md-notif-btn{position:relative}
  .md-notif-badge{position:absolute;top:-4px;right:-4px;width:16px;height:16px;background:#f44336;border-radius:50%;font-size:.6rem;color:#fff;font-weight:700;display:flex;align-items:center;justify-content:center}
  .md-ban-notice{background:linear-gradient(135deg,#b71c1c,#c62828);padding:16px;text-align:center}
  .md-ban-notice h3{color:#fff;font-size:1rem;margin-bottom:6px}
  .md-ban-notice p{color:rgba(255,255,255,.85);font-size:.85rem;margin-bottom:12px;line-height:1.5}
  .md-ban-pay-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 24px;background:#fff;color:#b71c1c;border:none;border-radius:8px;font-weight:700;font-size:.9rem;cursor:pointer}
  .md-ban-pay-btn:disabled{opacity:.6;cursor:not-allowed}
  .md-warning-notice{background:#fff3e0;border-bottom:2px solid #ffb74d;padding:12px 16px;display:flex;align-items:flex-start;gap:10px}
  .md-warning-notice p{font-size:.85rem;color:#e65100;line-height:1.5}
  .md-break-banner{background:linear-gradient(135deg,#37474f,#546e7a);padding:12px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
  .md-break-banner span{color:#fff;font-size:.88rem;font-weight:600}
  .md-break-end-btn{padding:7px 16px;background:#fff;color:#37474f;border:none;border-radius:8px;font-weight:700;font-size:.82rem;cursor:pointer}
  .md-tabs{background:#fff;border-bottom:1px solid #e8eaf6;display:flex;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;position:sticky;top:72px;z-index:99}
  .md-tabs::-webkit-scrollbar{display:none}
  .md-tab{padding:14px 16px;border:none;background:none;font-family:'DM Sans',sans-serif;font-size:.83rem;font-weight:600;color:#888;cursor:pointer;border-bottom:2.5px solid transparent;white-space:nowrap;transition:all .15s;flex-shrink:0}
  .md-tab.active{color:#5c6bc0;border-bottom-color:#5c6bc0}
  .md-tab-badge{display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;background:#f44336;color:#fff;border-radius:50%;font-size:.65rem;font-weight:700;margin-left:5px}
  .md-content{max-width:900px;margin:0 auto;padding:20px 16px 80px}
  .md-stats{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px}
  @media(min-width:600px){.md-stats{grid-template-columns:repeat(4,1fr)}}
  .md-stat-card{background:#fff;border-radius:14px;padding:16px 14px;box-shadow:0 2px 8px rgba(92,107,192,.08);border:1px solid #e8eaf6;text-align:center}
  .md-stat-icon{font-size:1.4rem;margin-bottom:6px}
  .md-stat-val{font-size:1.4rem;font-weight:700;color:#1a1a2e;line-height:1}
  .md-stat-label{font-size:.72rem;color:#888;margin-top:4px}
  .md-commission{background:#fff;border-radius:14px;padding:16px;box-shadow:0 2px 8px rgba(92,107,192,.08);border:1px solid #e8eaf6;margin-bottom:20px}
  .md-commission h4{font-weight:700;font-size:.9rem;color:#1a1a2e;margin-bottom:10px}
  .md-commission-row{display:flex;justify-content:space-between;align-items:center;padding:6px 0;font-size:.85rem;border-bottom:1px solid #f5f5f5}
  .md-commission-row:last-child{border:none}
  .md-tier-badge{padding:3px 10px;border-radius:20px;font-size:.75rem;font-weight:700}
  .md-section{background:#fff;border-radius:14px;padding:18px 16px;box-shadow:0 2px 8px rgba(92,107,192,.08);border:1px solid #e8eaf6;margin-bottom:16px}
  .md-section-title{font-weight:700;font-size:.95rem;color:#1a1a2e;margin-bottom:14px;display:flex;align-items:center;gap:8px}
  .md-section-empty{text-align:center;color:#bbb;font-size:.875rem;padding:20px 0}
  .md-session-card{border:1.5px solid #e8eaf6;border-radius:12px;padding:14px;margin-bottom:10px;background:#fafbff;position:relative}
  .md-session-card.active-now{border-color:#e53935;background:#fff5f5}
  .md-session-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px;gap:8px}
  .md-session-user{font-weight:700;font-size:.92rem;color:#1a1a2e}
  .md-session-status{padding:3px 10px;border-radius:20px;font-size:.72rem;font-weight:700;flex-shrink:0}
  .md-session-status.active{background:#ffebee;color:#c62828}
  .md-session-status.upcoming{background:#e8eaf6;color:#3949ab}
  .md-session-status.completed{background:#e8f5e9;color:#2e7d32}
  .md-session-detail{font-size:.8rem;color:#888;margin-bottom:3px}
  .md-session-topic{font-size:.85rem;color:#555;margin-bottom:8px}
  .md-session-earning{font-size:.82rem;color:#4caf50;font-weight:600}
  .md-session-actions{display:flex;gap:8px;margin-top:10px;flex-wrap:wrap}
  .md-join-btn{padding:8px 16px;background:#e53935;color:#fff;border:none;border-radius:8px;font-weight:700;font-size:.82rem;cursor:pointer;animation:md-pulse 1.5s infinite}
  @keyframes md-pulse{0%,100%{box-shadow:0 0 0 0 rgba(229,57,53,.4)}50%{box-shadow:0 0 0 8px rgba(229,57,53,0)}}
  .md-action-btn{padding:7px 14px;border-radius:8px;font-size:.8rem;font-weight:600;cursor:pointer;border:none}
  .md-action-btn.primary{background:#5c6bc0;color:#fff}
  .md-action-btn.secondary{background:#f0f0f0;color:#555}
  .md-earnings-hero{background:linear-gradient(135deg,#3949ab,#5c6bc0);border-radius:14px;padding:20px;color:#fff;margin-bottom:16px}
  .md-earnings-total{font-size:2rem;font-weight:700;margin:4px 0}
  .md-earnings-label{font-size:.8rem;opacity:.8}
  .md-earnings-breakdown{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-top:16px}
  .md-earn-item{background:rgba(255,255,255,.15);border-radius:10px;padding:10px;text-align:center}
  .md-earn-item-val{font-size:1rem;font-weight:700}
  .md-earn-item-label{font-size:.7rem;opacity:.8;margin-top:2px}
  .md-tx-row{display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #f0f0f0}
  .md-tx-row:last-child{border:none}
  .md-tx-type{font-size:.82rem;color:#555}
  .md-tx-desc{font-size:.75rem;color:#aaa;margin-top:2px}
  .md-tx-amount{font-weight:700;font-size:.9rem}
  .md-tx-amount.earning{color:#4caf50}
  .md-tx-amount.deduction{color:#f44336}
  .md-report-card{border:1.5px solid #ffcdd2;border-radius:12px;padding:14px;background:#fff5f5;margin-bottom:10px}
  .md-report-card.resolved{border-color:#c8e6c9;background:#f9fbe7}
  .md-report-card-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:6px}
  .md-report-cat{font-weight:700;font-size:.875rem;color:#c62828}
  .md-report-cat.resolved{color:#2e7d32}
  .md-report-date{font-size:.75rem;color:#aaa}
  .md-report-desc{font-size:.82rem;color:#666;margin-bottom:6px}
  .md-report-status{font-size:.75rem;padding:3px 10px;border-radius:20px;display:inline-flex}
  .md-report-status.pending{background:#fff3e0;color:#e65100}
  .md-report-status.reviewed{background:#e8eaf6;color:#3949ab}
  .md-report-status.resolved{background:#e8f5e9;color:#2e7d32}
  .md-report-status.dismissed{background:#f5f5f5;color:#888}
  .md-profile-photo-wrap{display:flex;align-items:center;gap:16px;margin-bottom:20px}
  .md-profile-photo{width:72px;height:72px;border-radius:50%;object-fit:cover;border:3px solid #e8eaf6}
  .md-photo-placeholder{width:72px;height:72px;border-radius:50%;background:#e8eaf6;display:flex;align-items:center;justify-content:center;font-size:2rem;flex-shrink:0}
  .md-photo-upload-btn{padding:8px 16px;background:#e8eaf6;border:none;border-radius:8px;font-size:.82rem;font-weight:600;color:#3949ab;cursor:pointer;position:relative;overflow:hidden}
  .md-photo-upload-btn input{position:absolute;inset:0;opacity:0;cursor:pointer}
  .md-form-grid{display:grid;grid-template-columns:1fr;gap:14px}
  @media(min-width:540px){.md-form-grid{grid-template-columns:1fr 1fr}}
  .md-form-grid .full{grid-column:1/-1}
  .md-field label{display:block;font-size:.78rem;font-weight:600;color:#666;margin-bottom:6px;text-transform:uppercase;letter-spacing:.04em}
  .md-field input,.md-field textarea,.md-field select{width:100%;padding:10px 13px;border:1.5px solid #e8eaf6;border-radius:9px;font-family:'DM Sans',sans-serif;font-size:.9rem;color:#1a1a2e;background:#f8f9ff;outline:none;transition:border-color .2s}
  .md-field input:focus,.md-field textarea:focus{border-color:#5c6bc0;background:#fff}
  .md-chat-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:1000;display:flex;align-items:flex-end;justify-content:center}
  .md-chat-panel{background:#fff;border-radius:20px 20px 0 0;width:100%;max-width:560px;height:90vh;display:flex;flex-direction:column}
  @media(min-width:600px){.md-chat-overlay{align-items:center;padding:16px}.md-chat-panel{border-radius:20px;height:85vh}}
  .md-chat-header{padding:14px 16px;border-bottom:1px solid #e8eaf6;display:flex;align-items:center;justify-content:space-between;flex-shrink:0}
  .md-chat-title{font-weight:700;color:#1a1a2e}
  .md-chat-subtitle{font-size:.78rem;color:#888;margin-top:1px}
  .md-chat-tabs{display:flex;border-bottom:1px solid #e8eaf6;flex-shrink:0}
  .md-chat-tab{flex:1;padding:10px;border:none;background:none;font-size:.82rem;font-weight:600;color:#aaa;cursor:pointer;border-bottom:2px solid transparent;transition:all .15s}
  .md-chat-tab.active{color:#5c6bc0;border-bottom-color:#5c6bc0}
  .md-messages{flex:1;overflow-y:auto;padding:12px 16px;display:flex;flex-direction:column;gap:8px;-webkit-overflow-scrolling:touch}
  .md-msg{max-width:78%}
  .md-msg.sent{align-self:flex-end}
  .md-msg.received{align-self:flex-start}
  .md-msg-bubble{padding:9px 13px;border-radius:16px;font-size:.88rem;line-height:1.5;word-break:break-word}
  .md-msg.sent .md-msg-bubble{background:#5c6bc0;color:#fff;border-radius:16px 16px 3px 16px}
  .md-msg.received .md-msg-bubble{background:#f0f0f0;color:#1a1a2e;border-radius:3px 16px 16px 16px}
  .md-msg-time{font-size:.68rem;color:#aaa;margin-top:2px}
  .md-msg.sent .md-msg-time{text-align:right}
  .md-input-row{padding:10px 12px;border-top:1px solid #e8eaf6;display:flex;gap:8px;flex-shrink:0;background:#fff}
  .md-input-row input{flex:1;padding:9px 13px;border:1.5px solid #e8eaf6;border-radius:9px;font-size:.9rem;outline:none}
  .md-input-row input:focus{border-color:#5c6bc0}
  .md-send-btn{padding:9px 16px;background:#5c6bc0;color:#fff;border:none;border-radius:9px;font-weight:700;cursor:pointer}
  .md-video-wrap{flex:1;display:flex;flex-direction:column;overflow:hidden}
  .md-close-btn{width:30px;height:30px;border-radius:50%;background:#f0f0f0;border:none;cursor:pointer;font-size:1rem;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .md-toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#1a1a2e;color:#fff;padding:12px 20px;border-radius:10px;font-size:.88rem;font-weight:600;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.25);max-width:calc(100vw - 32px);text-align:center;animation:md-toast-in .3s ease}
  @keyframes md-toast-in{from{opacity:0;transform:translateX(-50%) translateY(10px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}
  .md-toast.success{background:#4caf50}
  .md-toast.error{background:#f44336}
  .md-spin{width:16px;height:16px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:md-spin .6s linear infinite;display:inline-block}
  @keyframes md-spin{to{transform:rotate(360deg)}}
  .md-save-btn{padding:12px 24px;background:#5c6bc0;color:#fff;border:none;border-radius:10px;font-weight:700;font-size:.9rem;cursor:pointer;margin-top:20px;width:100%}
  .md-save-btn:disabled{opacity:.5;cursor:not-allowed}
  .md-break-btn{padding:11px 20px;background:#455a64;color:#fff;border:none;border-radius:9px;font-weight:700;font-size:.88rem;cursor:pointer;width:100%;margin-top:12px;display:flex;align-items:center;justify-content:center;gap:8px}
  .md-break-btn:disabled{opacity:.5;cursor:not-allowed}
`;

export default function MentorDashboard() {
  const navigate = useNavigate();

  const [mentor,        setMentor]        = useState(null);
  const [appointments,  setAppointments]  = useState([]);
  const [wallet,        setWallet]        = useState(null);
  const [transactions,  setTransactions]  = useState([]);
  const [reports,       setReports]       = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState("overview");
  const [activeChat,    setActiveChat]    = useState(null);
  const [chatMessages,  setChatMessages]  = useState([]);
  const [newMessage,    setNewMessage]    = useState("");
  const [activeChatTab, setActiveChatTab] = useState("chat");
  const [showNotif,     setShowNotif]     = useState(false);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [banPaying,     setBanPaying]     = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [availSaving,   setAvailSaving]   = useState(false);
  const [breakLoading,  setBreakLoading]  = useState(false);
  const [dayOffs,       setDayOffs]       = useState([]);
  const [dayOffDate,    setDayOffDate]    = useState("");
  const [dayOffReason,  setDayOffReason]  = useState("");
  const [dayOffLoading, setDayOffLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(null);
  const [toast,         setToast]         = useState(null);
  const [profileEdit,   setProfileEdit]   = useState({});
  const [photoFile,     setPhotoFile]     = useState(null);
  const [availability,  setAvailability]  = useState([]);

  // FIX: Reactive 'now' — updates every 30s so active/upcoming/completed auto-reclassify
  const [now, setNow] = useState(new Date());

  const messagesEndRef = useRef(null);
  const timerRef       = useRef(null);
  // FIX: ref for activeChat to avoid stale closure in RT subscription
  const activeChatRef  = useRef(null);

  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  // Tick every 30s
  useEffect(() => {
    const ticker = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(ticker);
  }, []);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { navigate("/", { replace: true }); return; }
    initDashboard(user.email);
    const cleanup = subscribeRealtime(user.email);
    return () => { cleanup(); if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Init — single mentor fetch, then parallel ──────────────
  const initDashboard = async (email) => {
    setLoading(true);
    try {
      const { data: mentorData, error: mentorErr } = await supabase
        .from("mentors")
        .select("*")
        .eq("email", email)
        .single();

      if (mentorErr || !mentorData) { setLoading(false); return; }

      setMentor(mentorData);
      setProfileEdit({
        full_name:     mentorData.full_name,
        profession:    mentorData.profession,
        organization:  mentorData.organization,
        bio:           mentorData.bio || "",
        expertise:     mentorData.expertise || "",
        category:      mentorData.category || "general",
        session_price: mentorData.session_price || 200,
        languages:     (mentorData.languages || []).join(", "),
      });

      const mentorId = mentorData.id;

      const [apptRes, walletRes, txRes, reportRes, notifRes, availRes, dayOffsRes] = await Promise.all([
        supabase.from("appointments")
          .select("id,date,time,topic,message,status,user_name,user_email,user_phone,session_price,mentor_payout,session_confirmed_by_user,video_room_id")
          .eq("mentor_email", email)
          .order("date", { ascending: true })
          .order("time", { ascending: true }),
        supabase.from("mentor_wallets")
          .select("balance,total_earned,pending_payout,total_withdrawn")
          .eq("mentor_id", mentorId)
          .maybeSingle(),
        supabase.from("wallet_transactions")
          .select("id,type,description,amount,recipient,created_at")
          .eq("mentor_id", mentorId)
          .order("created_at", { ascending: false })
          .limit(30),
        supabase.from("mentor_reports")
          .select("id,category,description,status,admin_note,created_at")
          .eq("mentor_id", mentorId)
          .order("created_at", { ascending: false }),
        supabase.from("mentor_notifications")
          .select("id,title,message,is_read,created_at")
          .eq("recipient", email)
          .order("created_at", { ascending: false })
          .limit(25),
        supabase.from("mentor_availability")
          .select("day_of_week,start_time,end_time")
          .eq("mentor_id", mentorId),
        supabase.from("mentor_day_offs")
          .select("date,reason")
          .eq("mentor_id", mentorId)
          .gte("date", new Date().toISOString().split("T")[0])
          .order("date", { ascending: true }),
      ]);

      if (apptRes.data) {
        // Normalize time on all appointments so date math is always correct
        setAppointments(apptRes.data.map(a => ({ ...a, time: normalizeTime(a.time) })));
      }
      if (walletRes.data)  setWallet(walletRes.data);
      if (txRes.data)      setTransactions(txRes.data);
      if (reportRes.data)  setReports(reportRes.data);
      if (notifRes.data) {
        setNotifications(notifRes.data);
        setUnreadCount(notifRes.data.filter(n => !n.is_read).length);
      }
      if (availRes.data) {
        const avail = availRes.data || [];
        setAvailability(DAYS.map((day, i) => {
          const ex = avail.find(a => a.day_of_week === i + 1);
          return { day, dayNum: i + 1, enabled: !!ex, startTime: ex?.start_time?.slice(0,5) || "09:00", endTime: ex?.end_time?.slice(0,5) || "17:00" };
        }));
      }
      if (dayOffsRes?.data) setDayOffs(dayOffsRes.data);
    } catch (err) {
      console.error("initDashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Realtime ─────────────────────────────────────────────────
  const subscribeRealtime = (email) => {
    const channel = supabase
      .channel("mentor-dashboard")
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"appointments", filter:`mentor_email=eq.${email}` }, (p) => {
        setAppointments(prev => [...prev, { ...p.new, time: normalizeTime(p.new.time) }]);
        showToast("📅 New booking received!", "success");
      })
      .on("postgres_changes", { event:"UPDATE", schema:"public", table:"appointments", filter:`mentor_email=eq.${email}` }, (p) => {
        setAppointments(prev => prev.map(a => a.id === p.new.id ? { ...a, ...p.new, time: normalizeTime(p.new.time) } : a));
      })
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"mentor_notifications", filter:`recipient=eq.${email}` }, (p) => {
        setNotifications(prev => [p.new, ...prev]);
        setUnreadCount(c => c + 1);
      })
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"chat_messages" }, (p) => {
        const current = activeChatRef.current;
        if (current && p.new.chat_session_id === current.id && p.new.sender_role === "user") {
          setChatMessages(prev => prev.some(m => m.id === p.new.id) ? prev : [...prev, p.new]);
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior:"smooth" }), 50);
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  };

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  /**
   * FIX: Session classification is now reactive (uses 'now' state that updates every 30s).
   * Previously 'now' was a static const in render — it never updated.
   *
   * ACTIVE:    start <= now < end (end = start + 60min)
   *            e.g. 9pm session is active from 9:00pm–9:59pm
   * UPCOMING:  start > now (not started)
   * COMPLETED: end <= now (60min window fully passed)
   */
  const activeSessions = appointments.filter(a => {
    if (a.status !== "confirmed") return false;
    const start = buildSessionDate(a.date, a.time);
    const end   = new Date(start.getTime() + 3600000);
    return start <= now && end > now;
  });

  const upcomingSessions = appointments.filter(a => {
    if (a.status !== "confirmed") return false;
    const start = buildSessionDate(a.date, a.time);
    return start > now;
  });

  const completedSessions = appointments.filter(a => {
    if (["cancelled_by_user","cancelled_by_mentor","refunded"].includes(a.status)) return false;
    const start = buildSessionDate(a.date, a.time);
    const end   = new Date(start.getTime() + 3600000);
    return ["completed","completed_with_feedback"].includes(a.status) || end <= now;
  });

  // ── Open chat ─────────────────────────────────────────────────
  const openSessionChat = async (appointment) => {
    let { data: chat } = await supabase
      .from("chat_sessions")
      .select("*")
      .eq("appointment_id", appointment.id)
      .maybeSingle();

    if (!chat) {
      const start = buildSessionDate(appointment.date, appointment.time);
      const { data } = await supabase.from("chat_sessions").insert({
        appointment_id:  appointment.id,
        mentor_id:       mentor.id,
        status:          "active",
        scheduled_start: start.toISOString(),
        scheduled_end:   new Date(start.getTime() + 3600000).toISOString(),
      }).select().single();
      chat = data;
    }

    const { data: msgs } = await supabase
      .from("chat_messages").select("*")
      .eq("chat_session_id", chat.id)
      .order("created_at", { ascending: true });

    const chatObj = { ...chat, appointment };
    setActiveChat(chatObj);
    activeChatRef.current = chatObj;
    setChatMessages(msgs || []);
    setActiveChatTab("chat");
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior:"smooth" }), 100);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeChat) return;
    const senderName = mentor.full_name;
    const tempMsg = {
      id: `temp-${Date.now()}`, chat_session_id: activeChat.id,
      sender_role: "mentor", sender_name: senderName,
      message: newMessage, created_at: new Date().toISOString(),
    };
    setChatMessages(prev => [...prev, tempMsg]);
    setNewMessage("");
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior:"smooth" }), 50);

    const { data, error } = await supabase.from("chat_messages").insert({
      chat_session_id: activeChat.id,
      appointment_id:  activeChat.appointment_id,
      sender_role:     "mentor",
      sender_name:     senderName,
      message:         tempMsg.message,
    }).select().single();

    if (error) {
      setChatMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      showToast("Failed to send", "error");
    } else {
      setChatMessages(prev => prev.map(m => m.id === tempMsg.id ? data : m));
    }
  };

  // ── Ban penalty payment ───────────────────────────────────────
  const handlePayPenalty = async () => {
    setBanPaying(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/payment/create-penalty-order`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mentorEmail: mentor.email }),
      });
      const { orderId } = await res.json();
      if (!window.Razorpay) throw new Error("Payment gateway not loaded");

      const rzp = new window.Razorpay({
        key: RAZORPAY_KEY, amount: 100000, currency: "INR",
        name: "HealHub", description: "Account Restoration Penalty",
        order_id: orderId,
        prefill: { name: mentor.full_name, email: mentor.email },
        theme: { color: "#5c6bc0" },
        handler: async (response) => {
          const verifyRes = await fetch(`${BACKEND_URL}/api/payment/penalty-pay`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ mentorEmail: mentor.email, ...response }),
          });
          if (verifyRes.ok) {
            showToast("✅ Account restored! Welcome back.", "success");
            setMentor(prev => ({ ...prev, is_banned: false, ban_reason: null }));
          } else {
            showToast("Payment verification failed. Contact support.", "error");
          }
          setBanPaying(false);
        },
        modal: { ondismiss: () => setBanPaying(false) },
      });
      rzp.open();
    } catch (err) {
      showToast(err.message || "Payment failed", "error");
      setBanPaying(false);
    }
  };

  // ── Mentor cancel session ─────────────────────────────────────
  const handleMentorCancelSession = async (appointment) => {
    const hoursUntil = (buildSessionDate(appointment.date, appointment.time) - now) / 36e5;
    const isLate     = hoursUntil < 6;
    const confirm    = window.confirm(
      isLate
        ? `⚠️ Cancelling less than 6 hours before the session.\n\nA ₹50 fine will be deducted from your next payout.\n\nThe user will receive a full refund.\n\nProceed?`
        : `Cancel this session with ${appointment.user_name || appointment.user_email}?\n\nThe user will receive a full refund.\n\nProceed?`
    );
    if (!confirm) return;

    setCancelLoading(appointment.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mentor-schedule/cancel-session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appointmentId: appointment.id, mentorEmail: mentor.email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Cancel failed");

      setAppointments(prev => prev.map(a =>
        a.id === appointment.id ? { ...a, status: "cancelled_by_mentor" } : a
      ));
      showToast(data.message, isLate ? "error" : "success");
    } catch (err) {
      showToast(err.message || "Failed to cancel session", "error");
    } finally {
      setCancelLoading(null);
    }
  };

  // ── Day off handlers ──────────────────────────────────────────
  const handleAddDayOff = async () => {
    if (!dayOffDate) return;
    setDayOffLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mentor-schedule/day-off/add`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mentorEmail: mentor.email, date: dayOffDate, reason: dayOffReason }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.sessions?.length) {
          showToast(`You have ${data.sessions.length} session(s) on this date. Cancel them first.`, "error");
        } else {
          showToast(data.error || "Failed to add day off", "error");
        }
        return;
      }
      setDayOffs(prev => [...prev, { date: dayOffDate, reason: dayOffReason }]
        .sort((a, b) => a.date.localeCompare(b.date)));
      setDayOffDate(""); setDayOffReason("");
      showToast(`Day off added for ${dayOffDate} ✅`);
    } catch (err) {
      showToast("Failed to add day off", "error");
    } finally {
      setDayOffLoading(false);
    }
  };

  const handleRemoveDayOff = async (date) => {
    try {
      await fetch(`${BACKEND_URL}/api/mentor-schedule/day-off/remove`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mentorEmail: mentor.email, date }),
      });
      setDayOffs(prev => prev.filter(d => d.date !== date));
      showToast(`Day off removed for ${date}`);
    } catch {
      showToast("Failed to remove day off", "error");
    }
  };

  const handleTakeBreak = async () => {
    setBreakLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mentor/take-break`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mentorEmail: mentor.email }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.sessions?.length) {
          showToast(`Cancel your ${data.sessions.length} upcoming session(s) first.`, "error");
        } else {
          showToast(data.message || "Cannot take break right now.", "error");
        }
        return;
      }
      setMentor(prev => ({ ...prev, is_on_break: true, is_active: false }));
      showToast("Break activated. Profile hidden from users.", "success");
    } catch {
      showToast("Failed to activate break.", "error");
    } finally {
      setBreakLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setBreakLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mentor/end-break`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mentorEmail: mentor.email }),
      });
      if (res.ok) {
        setMentor(prev => ({ ...prev, is_on_break: false, is_active: true }));
        showToast("Welcome back! Your profile is now visible.", "success");
      }
    } catch {
      showToast("Failed to end break.", "error");
    } finally {
      setBreakLoading(false);
    }
  };

  // ── Save profile ──────────────────────────────────────────────
  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      let photoUrl = mentor.profile_photo_url;
      if (photoFile) {
        const ext  = photoFile.name.split(".").pop();
        const path = `profiles/${mentor.id}/photo.${ext}`;
        const { data, error } = await supabase.storage
          .from("mentor-photos")
          .upload(path, photoFile, { upsert: true });
        if (!error) {
          const { data: u } = supabase.storage.from("mentor-photos").getPublicUrl(data.path);
          photoUrl = u.publicUrl;
        }
      }
      const { error } = await supabase.from("mentors").update({
        full_name:         profileEdit.full_name,
        profession:        profileEdit.profession,
        organization:      profileEdit.organization,
        bio:               profileEdit.bio,
        expertise:         profileEdit.expertise,
        category:          profileEdit.category,
        session_price:     parseInt(profileEdit.session_price, 10),
        languages:         profileEdit.languages.split(",").map(l => l.trim()).filter(Boolean),
        profile_photo_url: photoUrl,
        updated_at:        new Date().toISOString(),
      }).eq("id", mentor.id);
      if (error) throw error;
      setMentor(prev => ({ ...prev, ...profileEdit, profile_photo_url: photoUrl }));
      showToast("Profile saved ✅");
    } catch (err) {
      showToast("Save failed: " + (err.message || "Unknown error"), "error");
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Save availability ─────────────────────────────────────────
  const saveAvailability = async () => {
    if (!mentor?.id) return;
    setAvailSaving(true);
    try {
      await supabase.from("mentor_availability").delete().eq("mentor_id", mentor.id);
      const toInsert = availability.filter(a => a.enabled).map(a => ({
        mentor_id:   mentor.id,
        day_of_week: a.dayNum,
        start_time:  `${a.startTime}:00`,
        end_time:    `${a.endTime}:00`,
      }));
      if (toInsert.length) await supabase.from("mentor_availability").insert(toInsert);
      showToast("Availability updated ✅");
    } catch {
      showToast("Failed to save availability.", "error");
    } finally {
      setAvailSaving(false);
    }
  };

  const markNotificationsRead = async () => {
    if (!mentor?.email) return;
    await supabase.from("mentor_notifications")
      .update({ is_read: true })
      .eq("recipient", mentor.email)
      .eq("is_read", false);
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  // Helper: format time for display
  const formatDisplayTime = (rawTime) => {
    const t = normalizeTime(rawTime);
    const [h, m] = t.split(":");
    let hour = parseInt(h, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    if (hour > 12) hour -= 12;
    if (hour === 0) hour = 12;
    return `${String(hour).padStart(2,"0")}:${m} ${ampm}`;
  };

  const commTier = mentor ? calcCommissionLabel(mentor.rating, mentor.total_reviews) : { label:"Standard", pct:65, color:"#5c6bc0" };

  // ── Loading / No mentor screens ───────────────────────────────
  if (loading) return (
    <>
      <style>{css}</style>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",flexDirection:"column",gap:12,fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ width:40,height:40,border:"3px solid #e8eaf6",borderTopColor:"#5c6bc0",borderRadius:"50%",animation:"md-spin .6s linear infinite" }} />
        <span style={{ color:"#888",fontSize:".9rem" }}>Loading dashboard…</span>
      </div>
    </>
  );

  if (!mentor) return (
    <>
      <style>{css}</style>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",flexDirection:"column",gap:16,padding:24,textAlign:"center",fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ fontSize:"3rem" }}>🔍</div>
        <h2 style={{ color:"#1a1a2e" }}>No mentor profile found</h2>
        <p style={{ color:"#888",fontSize:".9rem" }}>Your application may still be under review.</p>
        <button onClick={() => navigate("/mentor-verification")}
          style={{ padding:"12px 24px",background:"#5c6bc0",color:"#fff",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer" }}>
          Apply as Mentor →
        </button>
        <button onClick={() => navigate("/dashboard")}
          style={{ padding:"10px 24px",background:"#f0f0f0",color:"#555",border:"none",borderRadius:10,fontWeight:600,cursor:"pointer" }}>
          Back to Dashboard
        </button>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      {!window.Razorpay && <script src="https://checkout.razorpay.com/v1/checkout.js" async />}

      <div className="md-root">
        {/* ── Header — FIX: Only ONE 🏠 home button, removed duplicate ── */}
        <div className="md-header">
          {mentor.profile_photo_url
            ? <img className="md-header-avatar" src={mentor.profile_photo_url} alt={mentor.full_name} />
            : <div className="md-header-avatar-placeholder">👤</div>
          }
          <div className="md-header-info">
            <div className="md-header-name">{mentor.full_name}</div>
            <div className="md-header-role">{mentor.profession}</div>
          </div>
          <div className="md-header-right">
            <button className="md-header-btn md-notif-btn"
              onClick={() => { setShowNotif(v => !v); if (unreadCount > 0) markNotificationsRead(); }}>
              🔔 {unreadCount > 0 && <span className="md-notif-badge">{unreadCount}</span>}
            </button>
            {/* Single home button — routes to /mentordashboard */}
            <button className="md-header-btn" onClick={() => navigate("/mentordashboard")}>🏠 Home</button>
          </div>
        </div>

        {/* Notification panel */}
        {showNotif && (
          <div style={{ position:"fixed",top:72,right:16,width:320,background:"#fff",borderRadius:12,boxShadow:"0 8px 32px rgba(0,0,0,.15)",zIndex:500,maxHeight:400,overflow:"auto",border:"1px solid #e8eaf6" }}>
            <div style={{ padding:"12px 16px",borderBottom:"1px solid #e8eaf6",fontWeight:700,fontSize:".875rem",display:"flex",justifyContent:"space-between" }}>
              Notifications
              <button onClick={() => setShowNotif(false)} style={{ background:"none",border:"none",cursor:"pointer",color:"#888" }}>✕</button>
            </div>
            {notifications.length === 0
              ? <div style={{ padding:24,textAlign:"center",color:"#bbb",fontSize:".85rem" }}>No notifications yet</div>
              : notifications.map(n => (
                <div key={n.id} style={{ padding:"10px 14px",borderBottom:"1px solid #f5f5f5",background:n.is_read?"#fff":"#f0f4ff" }}>
                  <div style={{ fontWeight:600,fontSize:".82rem" }}>{n.title}</div>
                  <div style={{ fontSize:".78rem",color:"#666",marginTop:2 }}>{n.message}</div>
                  <div style={{ fontSize:".7rem",color:"#bbb",marginTop:4 }}>{new Date(n.created_at).toLocaleDateString()}</div>
                </div>
              ))}
          </div>
        )}

        {/* ── Ban notice ── */}
        {mentor.is_banned && (
          <div className="md-ban-notice">
            <h3>🔴 Your account has been suspended</h3>
            <p>{mentor.ban_reason || "Your account was suspended due to community guideline violations."}<br/>Pay ₹1,000 penalty to restore access.</p>
            <button className="md-ban-pay-btn" onClick={handlePayPenalty} disabled={banPaying}>
              {banPaying ? <><span className="md-spin"/> Processing…</> : "💳 Pay ₹1,000 & Restore Account"}
            </button>
          </div>
        )}

        {/* ── Break banner ── */}
        {mentor.is_on_break && !mentor.is_banned && (
          <div className="md-break-banner">
            <span>😴 You're on a break — profile is hidden from users</span>
            <button className="md-break-end-btn" onClick={handleEndBreak} disabled={breakLoading}>
              {breakLoading ? "…" : "End Break →"}
            </button>
          </div>
        )}

        {/* ── Rating warning ── */}
        {!mentor.is_banned && mentor.total_reviews >= 5 && mentor.rating < 2.5 && (
          <div className="md-warning-notice">
            <span style={{ fontSize:"1.2rem" }}>⚠️</span>
            <p><strong>Rating Warning:</strong> Your current rating is {mentor.rating}★. Accounts with 20+ reports and a rating below 2.0★ are automatically suspended.</p>
          </div>
        )}

        {/* ── Tabs ── */}
        <div className="md-tabs">
          {[
            { id:"overview",  label:"📊 Overview" },
            { id:"sessions",  label:"📅 Sessions", badge: activeSessions.length },
            { id:"earnings",  label:"💰 Earnings" },
            { id:"profile",   label:"✏️ Profile" },
            { id:"reports",   label:"🚩 Reports",  badge: reports.filter(r => r.status === "pending").length },
            { id:"settings",  label:"⚙️ Settings" },
          ].map(t => (
            <button key={t.id} className={`md-tab ${activeTab === t.id ? "active" : ""}`}
              onClick={() => setActiveTab(t.id)}>
              {t.label}
              {t.badge > 0 && <span className="md-tab-badge">{t.badge}</span>}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div className="md-content">

          {/* ── OVERVIEW ── */}
          {activeTab === "overview" && (
            <>
              <div className="md-stats">
                {[
                  { icon:"📅", val: upcomingSessions.length,     label:"Upcoming" },
                  { icon:"✅", val: mentor.sessions_count || 0,   label:"Total Sessions" },
                  { icon:"⭐", val: mentor.total_reviews >= 3 ? `${mentor.rating}★` : "New", label:`${mentor.total_reviews} Reviews` },
                  { icon:"💰", val: `₹${wallet?.total_earned || 0}`, label:"Total Earned" },
                ].map(s => (
                  <div key={s.label} className="md-stat-card">
                    <div className="md-stat-icon">{s.icon}</div>
                    <div className="md-stat-val">{s.val}</div>
                    <div className="md-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="md-commission">
                <h4>Your Commission Tier</h4>
                <div className="md-commission-row">
                  <span>Current Tier</span>
                  <span className="md-tier-badge" style={{ background: commTier.color + "20", color: commTier.color }}>{commTier.label}</span>
                </div>
                <div className="md-commission-row"><span>Your share per session</span><span style={{ color: commTier.color, fontWeight:700 }}>{commTier.pct}%</span></div>
                <div className="md-commission-row"><span>On ₹{mentor.session_price} session</span><span style={{ fontWeight:700 }}>₹{Math.floor(mentor.session_price * commTier.pct / 100)}</span></div>
                <div style={{ fontSize:".78rem",color:"#888",marginTop:8,lineHeight:1.5 }}>
                  💡 Reach 4.5★ with 3+ reviews to upgrade to Premium Tier (75%).
                </div>
              </div>

              {activeSessions.length > 0 && (
                <div className="md-section">
                  <div className="md-section-title">🔴 Active Sessions Now</div>
                  {activeSessions.map(a => (
                    <div key={a.id} className="md-session-card active-now">
                      <div className="md-session-top"><div className="md-session-user">{a.user_name || a.user_email}</div><span className="md-session-status active">Live</span></div>
                      <div className="md-session-detail">⏰ {a.date} at {formatDisplayTime(a.time)}</div>
                      <div className="md-session-topic">📝 {a.topic}</div>
                      <div className="md-session-actions">
                        <button className="md-join-btn" onClick={() => openSessionChat(a)}>💬 Chat</button>
                        <button className="md-action-btn primary" onClick={() => { openSessionChat(a); setActiveChatTab("video"); }}>📹 Video Call</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="md-section">
                <div className="md-section-title">📅 Upcoming Sessions ({upcomingSessions.length})</div>
                {upcomingSessions.length === 0
                  ? <div className="md-section-empty">No upcoming sessions. Your profile is live! ✨</div>
                  : upcomingSessions.slice(0,5).map(a => (
                    <div key={a.id} className="md-session-card">
                      <div className="md-session-top"><div className="md-session-user">{a.user_name || a.user_email}</div><span className="md-session-status upcoming">Upcoming</span></div>
                      <div className="md-session-detail">📅 {a.date} at {formatDisplayTime(a.time)}</div>
                      <div className="md-session-topic">📝 {a.topic}</div>
                      <div className="md-session-earning">💰 You earn ₹{Math.floor((a.session_price || mentor.session_price) * commTier.pct / 100)}</div>
                    </div>
                  ))
                }
              </div>
            </>
          )}

          {/* ── SESSIONS ── */}
          {activeTab === "sessions" && (
            <>
              {activeSessions.length > 0 && (
                <div className="md-section">
                  <div className="md-section-title">🔴 Active Now</div>
                  {activeSessions.map(a => (
                    <div key={a.id} className="md-session-card active-now">
                      <div className="md-session-top"><div className="md-session-user">{a.user_name || a.user_email}</div><span className="md-session-status active">Live</span></div>
                      <div className="md-session-detail">📅 {a.date} · {a.user_email}</div>
                      <div className="md-session-topic">📝 {a.topic}</div>
                      {a.message && <div className="md-session-detail" style={{ fontStyle:"italic" }}>"{a.message}"</div>}
                      <div className="md-session-actions">
                        <button className="md-join-btn" onClick={() => openSessionChat(a)}>💬 Chat</button>
                        <button className="md-action-btn primary" onClick={() => { openSessionChat(a); setActiveChatTab("video"); }}>📹 Video</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="md-section">
                <div className="md-section-title">📅 Upcoming ({upcomingSessions.length})</div>
                {upcomingSessions.length === 0
                  ? <div className="md-section-empty">No upcoming sessions</div>
                  : upcomingSessions.map(a => (
                    <div key={a.id} className="md-session-card">
                      <div className="md-session-top"><div className="md-session-user">{a.user_name || a.user_email}</div><span className="md-session-status upcoming">Upcoming</span></div>
                      <div className="md-session-detail">📅 {a.date} at {formatDisplayTime(a.time)}</div>
                      <div className="md-session-detail">📧 {a.user_email}{a.user_phone ? ` · 📱 ${a.user_phone}` : ""}</div>
                      <div className="md-session-topic">📝 {a.topic}</div>
                      {a.message && <div style={{ fontSize:".8rem",fontStyle:"italic",color:"#888",marginBottom:6 }}>"{a.message}"</div>}
                      <div className="md-session-earning">💰 Earning: ₹{Math.floor((a.session_price || mentor.session_price) * commTier.pct / 100)}</div>
                      <div className="md-session-actions">
                        <button className="md-action-btn secondary"
                          disabled={cancelLoading === a.id}
                          onClick={() => handleMentorCancelSession(a)}
                          style={{ background:"#ffebee",color:"#c62828" }}>
                          {cancelLoading === a.id ? "Cancelling…" : "Cancel Session"}
                        </button>
                      </div>
                    </div>
                  ))
                }
              </div>
              <div className="md-section">
                <div className="md-section-title">✅ Completed ({completedSessions.length})</div>
                {completedSessions.length === 0
                  ? <div className="md-section-empty">No completed sessions yet</div>
                  : completedSessions.slice(0,10).map(a => (
                    <div key={a.id} className="md-session-card">
                      <div className="md-session-top"><div className="md-session-user">{a.user_name || a.user_email}</div><span className="md-session-status completed">Done</span></div>
                      <div className="md-session-detail">📅 {a.date} at {formatDisplayTime(a.time)}</div>
                      <div className="md-session-topic">📝 {a.topic}</div>
                      <div className="md-session-earning">💰 Earned: ₹{a.mentor_payout || Math.floor((a.session_price || mentor.session_price) * commTier.pct / 100)}</div>
                    </div>
                  ))
                }
              </div>
            </>
          )}

          {/* ── EARNINGS ── */}
          {activeTab === "earnings" && (
            <>
              <div className="md-earnings-hero">
                <div className="md-earnings-label">Total Earned</div>
                <div className="md-earnings-total">₹{wallet?.total_earned || 0}</div>
                <div className="md-earnings-breakdown">
                  <div className="md-earn-item"><div className="md-earn-item-val">₹{wallet?.balance || 0}</div><div className="md-earn-item-label">Available</div></div>
                  <div className="md-earn-item"><div className="md-earn-item-val">₹{wallet?.pending_payout || 0}</div><div className="md-earn-item-label">Pending</div></div>
                  <div className="md-earn-item"><div className="md-earn-item-val">₹{wallet?.total_withdrawn || 0}</div><div className="md-earn-item-label">Paid Out</div></div>
                </div>
              </div>
              <div className="md-section">
                <div className="md-section-title">📋 Transaction History</div>
                {transactions.length === 0
                  ? <div className="md-section-empty">No transactions yet</div>
                  : transactions.map(tx => (
                    <div key={tx.id} className="md-tx-row">
                      <div>
                        <div className="md-tx-type">{tx.type.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</div>
                        <div className="md-tx-desc">{tx.description}</div>
                        <div style={{ fontSize:".7rem",color:"#bbb",marginTop:1 }}>{new Date(tx.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className={`md-tx-amount ${tx.recipient === "mentor" ? "earning" : "deduction"}`}>
                        {tx.recipient === "mentor" ? "+" : "-"}₹{tx.amount}
                      </div>
                    </div>
                  ))
                }
              </div>
            </>
          )}

          {/* ── PROFILE ── */}
          {activeTab === "profile" && (
            <div className="md-section">
              <div className="md-section-title">✏️ Edit Profile</div>
              <div className="md-profile-photo-wrap">
                {(mentor.profile_photo_url || photoFile)
                  ? <img className="md-profile-photo" src={photoFile ? URL.createObjectURL(photoFile) : mentor.profile_photo_url} alt="Profile" />
                  : <div className="md-photo-placeholder">👤</div>
                }
                <div>
                  <div className="md-photo-upload-btn">
                    📷 Change Photo
                    <input type="file" accept="image/*" onChange={e => setPhotoFile(e.target.files[0])} />
                  </div>
                  <div style={{ fontSize:".72rem",color:"#aaa",marginTop:4 }}>JPG, PNG · Max 2MB</div>
                </div>
              </div>
              <div className="md-form-grid">
                {[
                  { key:"full_name",    label:"Full Name",                  type:"text" },
                  { key:"profession",   label:"Profession",                 type:"text" },
                  { key:"organization", label:"Organization",               type:"text" },
                  { key:"expertise",    label:"Expertise (comma-sep)",      type:"text" },
                  { key:"languages",    label:"Languages (comma-sep)",      type:"text" },
                  { key:"session_price",label:"Session Price (₹150–₹400)",  type:"number" },
                ].map(({ key, label, type }) => (
                  <div className="md-field" key={key}>
                    <label>{label}</label>
                    <input type={type} value={profileEdit[key] || ""}
                      min={type === "number" ? 150 : undefined}
                      max={type === "number" ? 400 : undefined}
                      onChange={e => setProfileEdit(p => ({ ...p, [key]: e.target.value }))} />
                  </div>
                ))}
                <div className="md-field">
                  <label>Mentoring Category</label>
                  <select value={profileEdit.category || "general"}
                    onChange={e => setProfileEdit(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div className="md-field full">
                  <label>Bio (min 80 characters)</label>
                  <textarea rows={5} value={profileEdit.bio || ""}
                    onChange={e => setProfileEdit(p => ({ ...p, bio: e.target.value }))}
                    placeholder="Describe your approach and expertise…" />
                </div>
              </div>
              <button className="md-save-btn" onClick={saveProfile} disabled={profileSaving}>
                {profileSaving ? <><span className="md-spin" style={{ display:"inline-block" }} /> Saving…</> : "Save Profile Changes"}
              </button>
            </div>
          )}

          {/* ── REPORTS ── */}
          {activeTab === "reports" && (
            <div className="md-section">
              <div className="md-section-title">🚩 Reports Against You ({reports.length})</div>
              <div style={{ fontSize:".82rem",color:"#888",marginBottom:16,lineHeight:1.6 }}>
                Reports are reviewed within 48 hours. 20+ reports with rating below 2.0★ triggers auto-suspension.
              </div>
              {reports.length === 0
                ? <div className="md-section-empty">✅ No reports — keep it up!</div>
                : reports.map(r => (
                  <div key={r.id} className={`md-report-card ${["resolved","dismissed"].includes(r.status) ? "resolved" : ""}`}>
                    <div className="md-report-card-top">
                      <div className={`md-report-cat ${["resolved","dismissed"].includes(r.status) ? "resolved" : ""}`}>
                        {r.category.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}
                      </div>
                      <div className="md-report-date">{new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                    {r.description && <div className="md-report-desc">"{r.description}"</div>}
                    <span className={`md-report-status ${r.status}`}>{r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>
                    {r.admin_note && (
                      <div style={{ fontSize:".78rem",color:"#555",marginTop:8,padding:"8px 10px",background:"#f9f9f9",borderRadius:6 }}>
                        Admin note: {r.admin_note}
                      </div>
                    )}
                  </div>
                ))
              }
            </div>
          )}

          {/* ── SETTINGS ── */}
          {activeTab === "settings" && (
            <>
              <div className="md-section">
                <div className="md-section-title">📅 Availability Schedule</div>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%",borderCollapse:"collapse" }}>
                    <thead>
                      <tr>{["Day","Available","From","To"].map(h => (
                        <th key={h} style={{ textAlign:"left",padding:"6px 8px",fontSize:".75rem",fontWeight:700,color:"#888",textTransform:"uppercase",borderBottom:"2px solid #e8eaf6" }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {availability.map((a, i) => (
                        <tr key={a.day}>
                          <td style={{ padding:"6px 8px",fontSize:".85rem",fontWeight:600 }}>{a.day.slice(0,3)}</td>
                          <td style={{ padding:"6px 8px" }}>
                            <input type="checkbox" checked={a.enabled}
                              onChange={e => setAvailability(p => p.map((x,j) => j===i ? {...x,enabled:e.target.checked} : x))}
                              style={{ width:16,height:16,accentColor:"#5c6bc0" }} />
                          </td>
                          <td style={{ padding:"6px 8px" }}>
                            <select disabled={!a.enabled} value={a.startTime}
                              onChange={e => setAvailability(p => p.map((x,j) => j===i ? {...x,startTime:e.target.value} : x))}
                              style={{ padding:"4px 8px",border:"1px solid #e8eaf6",borderRadius:6,fontSize:".82rem" }}>
                              {TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </td>
                          <td style={{ padding:"6px 8px" }}>
                            <select disabled={!a.enabled} value={a.endTime}
                              onChange={e => setAvailability(p => p.map((x,j) => j===i ? {...x,endTime:e.target.value} : x))}
                              style={{ padding:"4px 8px",border:"1px solid #e8eaf6",borderRadius:6,fontSize:".82rem" }}>
                              {TIMES.filter(t => t > a.startTime).map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="md-save-btn" onClick={saveAvailability} disabled={availSaving}>
                  {availSaving ? "Saving…" : "Save Availability"}
                </button>
              </div>

              {/* Take a Break */}
              <div className="md-section">
                <div className="md-section-title">😴 Take a Break</div>
                <div style={{ fontSize:".85rem",color:"#666",lineHeight:1.7,marginBottom:12 }}>
                  Temporarily hide your profile from users. You must cancel all upcoming sessions first.
                  <strong style={{ color:"#f44336" }}> If your account remains on break for 28 consecutive days, it will be automatically deleted.</strong>
                </div>
                {mentor.is_on_break ? (
                  <button className="md-save-btn" onClick={handleEndBreak} disabled={breakLoading} style={{ background:"#4caf50" }}>
                    {breakLoading ? "…" : "✅ End Break — Go Active Again"}
                  </button>
                ) : (
                  <button className="md-break-btn" onClick={handleTakeBreak} disabled={breakLoading}>
                    {breakLoading ? <><span className="md-spin"/> Checking sessions…</> : "😴 Take a Break"}
                  </button>
                )}
              </div>

              {/* Day Off Scheduler */}
              <div className="md-section">
                <div className="md-section-title">🗓️ Schedule Day Off</div>
                <div style={{ fontSize:".85rem",color:"#666",lineHeight:1.7,marginBottom:16 }}>
                  Block specific dates.
                  <strong style={{ color:"#e65100" }}> Cancelling within 6 hours of a session = ₹50 fine from your next payout.</strong>
                </div>
                <div style={{ display:"flex",gap:10,flexWrap:"wrap",marginBottom:16 }}>
                  <input type="date" value={dayOffDate}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={e => setDayOffDate(e.target.value)}
                    style={{ padding:"9px 12px",border:"1.5px solid #e8eaf6",borderRadius:9,fontFamily:"'DM Sans',sans-serif",fontSize:".9rem",outline:"none",flex:1,minWidth:140 }} />
                  <input type="text" placeholder="Reason (optional)" value={dayOffReason}
                    onChange={e => setDayOffReason(e.target.value)}
                    style={{ padding:"9px 12px",border:"1.5px solid #e8eaf6",borderRadius:9,fontFamily:"'DM Sans',sans-serif",fontSize:".9rem",outline:"none",flex:2,minWidth:140 }} />
                  <button onClick={handleAddDayOff} disabled={!dayOffDate || dayOffLoading}
                    style={{ padding:"9px 18px",background:"#5c6bc0",color:"#fff",border:"none",borderRadius:9,fontWeight:700,cursor:"pointer",opacity:(!dayOffDate||dayOffLoading)?0.5:1,whiteSpace:"nowrap" }}>
                    {dayOffLoading ? "Adding…" : "+ Add Day Off"}
                  </button>
                </div>
                {dayOffs.length === 0
                  ? <div style={{ textAlign:"center",color:"#bbb",fontSize:".85rem",padding:"16px 0" }}>No day offs scheduled.</div>
                  : <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                    {dayOffs.map(d => (
                      <div key={d.date} style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:"#fff3e0",border:"1px solid #ffcc80",borderRadius:10 }}>
                        <div>
                          <div style={{ fontWeight:700,fontSize:".875rem",color:"#e65100" }}>
                            🚫 {new Date(d.date + "T12:00:00").toLocaleDateString("en-IN",{ weekday:"short",day:"numeric",month:"short",year:"numeric" })}
                          </div>
                          {d.reason && <div style={{ fontSize:".78rem",color:"#888",marginTop:2 }}>{d.reason}</div>}
                        </div>
                        <button onClick={() => handleRemoveDayOff(d.date)}
                          style={{ background:"#ffebee",color:"#c62828",border:"1px solid #ffcdd2",borderRadius:7,padding:"5px 12px",fontSize:".78rem",fontWeight:600,cursor:"pointer" }}>
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                }
                <div style={{ marginTop:14,padding:"12px 14px",background:"#e8eaf6",borderRadius:10,fontSize:".8rem",color:"#3949ab",lineHeight:1.6 }}>
                  💡 <strong>Fine policy:</strong> Cancel a session less than 6 hours before = ₹50 deducted from next payout.
                </div>
              </div>

              <div className="md-section">
                <div className="md-section-title">🚪 Account</div>
                <button onClick={() => { auth.signOut(); navigate("/"); }}
                  style={{ padding:"11px 20px",background:"#ffebee",color:"#c62828",border:"1px solid #ffcdd2",borderRadius:9,fontWeight:600,cursor:"pointer",width:"100%" }}>
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── CHAT / VIDEO OVERLAY ── */}
      {activeChat && (
        <div className="md-chat-overlay" onClick={e => e.target === e.currentTarget && setActiveChat(null)}>
          <div className="md-chat-panel">
            <div className="md-chat-header">
              <div>
                <div className="md-chat-title">{activeChat.appointment?.user_name || activeChat.appointment?.user_email}</div>
                <div className="md-chat-subtitle">📝 {activeChat.appointment?.topic}</div>
              </div>
              <button className="md-close-btn" onClick={() => { setActiveChat(null); activeChatRef.current = null; }}>✕</button>
            </div>
            <div className="md-chat-tabs">
              <button className={`md-chat-tab ${activeChatTab==="chat"?"active":""}`} onClick={() => setActiveChatTab("chat")}>💬 Chat</button>
              <button className={`md-chat-tab ${activeChatTab==="video"?"active":""}`} onClick={() => setActiveChatTab("video")}>📹 Video Call</button>
            </div>

            {activeChatTab === "chat" && (
              <>
                <div className="md-messages">
                  {chatMessages.length === 0
                    ? <div style={{ textAlign:"center",color:"#ccc",marginTop:40,fontSize:".85rem" }}>No messages yet. Say hello! 👋</div>
                    : chatMessages.map((msg, idx) => (
                      <div key={msg.id || idx} className={`md-msg ${msg.sender_role==="mentor"?"sent":"received"}`}>
                        <div className="md-msg-bubble">{msg.message}</div>
                        <div className="md-msg-time">{new Date(msg.created_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
                      </div>
                    ))
                  }
                  <div ref={messagesEndRef} />
                </div>
                <div className="md-input-row">
                  <input placeholder="Type your message…" value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} />
                  <button className="md-send-btn" onClick={sendMessage}>Send</button>
                </div>
              </>
            )}

            {activeChatTab === "video" && (
              <div className="md-video-wrap">
                <HealHubVideoRoom
                  roomId={activeChat.appointment?.video_room_id}
                  participantName={mentor.full_name}
                  role="HOST"
                  sessionEndTime={activeChat.scheduled_end}
                  onLeave={() => setActiveChatTab("chat")}
                  appointmentId={activeChat.appointment_id}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div className={`md-toast ${toast.type}`}>
          {toast.type === "success" ? "✓" : "✕"} {toast.msg}
        </div>
      )}
    </>
  );
}